import { WorkbenchUiAccessorType } from 'Core/Workbench'
import { TimerElement } from 'Layouts/Workbench/InformationElements/Timer/TimerElement'
import { ContainerType, ContextType, TaskConfigType, TaskMode, StepTypes } from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { ContextHandlerConfigType, IContextHandler } from 'Core/ContextHandler/IContextHandler'
import { AttributeHeaderTitle } from 'Components/tasks/Attributes/AttributeHeaderTitle'
import { TimerStartParams, TimerType } from 'Core/Timer'
import { createSidebarIcon } from 'Utils/component.utils'
import UnorderedListOutlined from '@ant-design/icons/lib/icons/UnorderedListOutlined'
import TaskInstruction from 'Components/tasks/TaskInstruction/TaskInstruction'
import TaskInformation from 'Components/tasks/TaskInformation/TaskInformation'
import { TaskInformationIcon } from 'Components/common/Icons/TaskInformationIcon'
import { NullUUID } from 'Constants/page.constants'
import { sendAnalyticsTrack } from 'Utils/analytics.utils'
import { TaskNavigator } from 'Components/tasks/TaskNavigator/TaskNavigator'
import { SubmitModalController } from 'Core/ModalCore/Modals/SubmitModalController'
import { RejectOrSkipModalController } from 'Core/ModalCore/Modals/RejectOrSkipModalController'
import { ActionModalController } from 'Core/ModalCore/Modals/ActionModalController'
import { ViolationsModalController } from 'Core/ModalCore/Modals/ViolationsModalController'
import { SaveForLaterModalController } from 'Core/ModalCore/Modals/SaveForLaterModalController'
import { InactivityModalController } from 'Core/ModalCore/Modals/InactivityModalController'
import { NotificationPropsType } from 'Types/Tasks.types'
import { ReworkCard } from 'Components/common/Cards/ReworkCard/ReworkCard'
import { JobPreviewNavigator } from 'Components/tasks/JobPreviewNavigator/JobPreviewNavigator'
import { isViewerMode } from 'Utils/task.utils'

export class TaskContextHandler<T extends ContainerType, U> implements IContextHandler<T, U> {
    private config: ContextHandlerConfigType<T, U>

    constructor(config: ContextHandlerConfigType<T, U>) {
        this.config = config
    }

    public getDefaultModals = () => [
        SubmitModalController.Id,
        RejectOrSkipModalController.Id,
        ActionModalController.Id,
        ViolationsModalController.Id,
        SaveForLaterModalController.Id,
        InactivityModalController.Id,
    ]

    private addTimerElement = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        const studio = this.config.getStudio()
        if (
            studio.studioConfig.context.type !== ContextType.NormalTask ||
            isViewerMode(studio.taskConfig.mode)
        ) {
            return
        }
        const timerElement = createReactElement(TimerElement, {
            getTimer: () => studio.taskManager.timer,
            handleTimerClick: () => {
                sendAnalyticsTrack('PnwElapsedTime_Clicked', {
                    task_id: studio.taskConfig.assignment.task.id,
                    user_id: studio.taskConfig.assignment.user_id,
                    project_id: studio.taskConfig.assignment.project_id,
                    step_id: studio.taskConfig.assignment.step?.id,
                })
            },
        })
        workbenchUiAccessor.toolbar.addInfoElement({
            id: 'timer',
            element: timerElement.element,
            alignment: 'center',
            placement: 'start',
        })
    }

    private readonly addJobPreviewNavigator = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        const studio = this.config.getStudio()
        if (studio.taskConfig.mode !== TaskMode.Viewer) {
            return
        }
        const outputHistory = studio.taskConfig.outputs?.taskOutput
        const taskData =
            outputHistory?.map(task => ({
                submittedAt: task.output.updated_at,
                stepName: task.stepName,
                taskId: task.taskId!,
                stepType: task.stepType,
            })) ?? []
        const jobPreviewNavigator = createReactElement(JobPreviewNavigator, {
            jobId: studio.taskConfig.assignment.task.job_id!,
            taskData,
            childJobs: studio.taskConfig.childJobs ?? [],
            steps: studio.taskConfig.steps ?? [],
        })
        workbenchUiAccessor.toolbar.addInfoElement({
            id: 'change-view',
            element: jobPreviewNavigator.element,
            alignment: 'center',
            placement: 'start',
        })
    }

    private readonly changeTaskView = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        const studio = this.config.getStudio()
        if (
            window.location.pathname.startsWith('/preview-on-hold/') ||
            studio.taskConfig.mode !== TaskMode.TaskViewer
        ) {
            return
        }
        const outputHistory = studio.taskConfig.assignment.output_history
        const taskData =
            outputHistory?.map(task => ({
                submittedAt: task.output.updated_at,
                stepName: task.step_name,
                taskId: task.task_id!,
                isCurrentUserStep: !!task.is_current_user_step,
            })) ?? []

        const changeViewElement = createReactElement(TaskNavigator, {
            taskData,
        })

        workbenchUiAccessor.toolbar.addInfoElement({
            id: 'change-view',
            element: changeViewElement.element,
            alignment: 'center',
            placement: 'start',
        })
    }

    public addInfoElements = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        this.addTimerElement(workbenchUiAccessor)
        this.changeTaskView(workbenchUiAccessor)
        this.addJobPreviewNavigator(workbenchUiAccessor)
        if (this.isReworkTask()) {
            this.addReworkCard()
        }
    }

    public addActions = (config: TaskConfigType<T, U>) => {
        switch (config.mode) {
            case TaskMode.Viewer:
            case TaskMode.TaskViewer:
                return
            case TaskMode.QC:
                this.config.actionFunctions.addQCActions(config)
                break
            case TaskMode.Reviewer:
                this.config.actionFunctions.addReviewerActions(config)
                break
            case TaskMode.Triager:
                this.config.actionFunctions.addTriagerActions(config)
                break
            default:
                this.config.actionFunctions.addMakerActions(config)
        }
    }

    private addReworkCard = () => {
        const workbenchUiAccessor = this.config.getStudio().workbench.workbenchUiAccessor
        if (!workbenchUiAccessor) return

        const reworkCardElement = createReactElement(ReworkCard, {})
        workbenchUiAccessor.toolbar.addInfoElement({
            id: 'rework-card',
            element: reworkCardElement.element,
            alignment: 'right',
            placement: 'start',
        })
    }

    public addLeftSidebarItems = (workbenchUiAccessor: WorkbenchUiAccessorType) => {
        // add instructions
        workbenchUiAccessor.leftSidebar.addItem({
            id: 'task-instructions',
            element: createReactElement(TaskInstruction, {
                instructions: this.config.getStudio().taskConfig.assignment.task.instructions,
                title: this.getInstructionTitle(),
            }).element,
            icon: createSidebarIcon(UnorderedListOutlined),
            title: this.getInstructionTitle(),
        })

        //  add task information
        workbenchUiAccessor.leftSidebar.addItem({
            id: 'task-information',
            element: createReactElement(TaskInformation, {
                informationList: [
                    {
                        label: 'Task ID',
                        value: this.config.getStudio().taskConfig.assignment.task.id,
                    },
                    {
                        label: 'Job ID',
                        value: this.config.getStudio().taskConfig.assignment.task.job_id,
                    },
                ],
                title: this.getInformationTitle(),
                expertDetails: this.getExpertDetails(),
            }).element,
            icon: TaskInformationIcon,
            title: this.getInformationTitle(),
        })
    }

    public createAttributeHeader = () => {
        return createReactElement(AttributeHeaderTitle, {}).element
    }

    public getInstructionTitle = () => {
        return 'Task Instructions'
    }

    public getInformationTitle = () => {
        return 'Task Information'
    }

    public isReadOnly = () => {
        const mode = this.config.getStudio().taskConfig.mode
        return ![TaskMode.Maker, TaskMode.Rework].includes(mode)
    }

    public shouldEnableViolations = () => {
        return !isViewerMode(this.config.getStudio().taskConfig.mode)
    }

    public startTimer = () => {
        const elapsedTime = this.config.getTaskState()?.submission.time_elapsed_in_seconds ?? 0
        const startConfig: TimerStartParams = {
            type: TimerType.CountUp,
            elapsedTimeInSec: elapsedTime,
        }
        this.config.getStudio().taskManager.timer.start(startConfig)
    }

    public getFeedback = () => {
        return undefined
    }

    isTrainingMode = (): boolean | undefined => {
        return undefined
    }

    private isReworkTask = (): boolean => {
        const studio = this.config.getStudio()
        const outputHistory = studio.taskConfig.assignment.output_history
        if (!outputHistory) return false
        const currentTaskIndex = outputHistory.findIndex(
            task => task.task_id === studio.taskConfig.assignment.task.id
        )

        // Check if any output has gone through QualityCheck step
        // This indicates the task has transitioned to rework mode
        return outputHistory
            .slice(0, currentTaskIndex)
            .some(output => output.step_type === StepTypes.QualityCheck)
    }

    public handleSaveSubmissionError = (
        errorMessage: string,
        _errorCode?: string,
        onComplete?: (props?: NotificationPropsType) => void
    ) => {
        if (errorMessage === 'Task submission cancelled by admin.') {
            // Handle admin cancellation
            if (onComplete) {
                onComplete({ type: 'error', message: 'Task submission cancelled by Admin' })
            } else {
                console.log('Task submission cancelled by admin')
            }
        }
    }

    private getExpertDetails = (): { id: string; stepName: string }[] => {
        const studio = this.config.getStudio()
        const outputHistory = studio.taskConfig.assignment.output_history
        const currentStep = studio.taskConfig.assignment.step

        if (!outputHistory) return []

        const filteredItems = outputHistory.filter(
            item => item.expert && item.expert.id !== NullUUID
        )

        if (currentStep?.type === StepTypes.Reviewer) {
            const mostRecentMaker = [...filteredItems]
                .reverse()
                .find(item => item.step_type === StepTypes.Maker)

            return mostRecentMaker
                ? [
                      {
                          id: mostRecentMaker.expert!.id,
                          stepName: mostRecentMaker.step_name,
                      },
                  ]
                : []
        }

        return filteredItems.map(item => ({
            id: item.expert!.id,
            stepName: item.step_name,
        }))
    }
}
